import React, { useEffect, useState } from "react";
import supabase from "../../../utils/supabase";
import { toast } from "react-toastify";
import useUserStore from "../../../stores/user";
import { Link, useNavigate } from "react-router-dom";
import { getRetakeInfo, handleRetakeAssignment } from "../../../utils/retakes";
import FeedbackModal from "../../modals/FeedbackModal";
import dayjs from "dayjs";
import { useUiLang } from "../../../stores/uiLang";
import { peCurriculum } from "../../../data/peCurriculum";

// Run E2 J2: student assignment list in English and Spanish (shared UI
// language). The PFL "standard" sort label (state standards) is now "Chapter".
const TABLE_T = {
  en: {
    title: "Current Assignments",
    sortBy: "Sort by:",
    dueDate: "Due Date",
    startDate: "Start Date",
    chapter: "Chapter",
    filterBy: "Filter by:",
    active: "Active Assignments",
    inProgress: "In Progress",
    notStarted: "Not Started",
    overdue: "Overdue",
    completed: "Completed",
    waiting: "Waiting for teacher",
    assignment: "Assignment",
    status: "Status",
    grade: "Grade",
    retakes: "Retakes",
    action: "Action",
    loading: "Loading assignments...",
    none: "No assignments found",
    daysLeft: (n: number) => `${n} ${n === 1 ? "day" : "days"} left`,
    dueToday: "Due today or past due",
    pending: "Pending review",
    start: "Start",
    continue: "Continue",
    completeNow: "Complete Now",
    view: "View",
    feedback: "Feedback",
    retake: "Retake",
    startsToday: "Starts today",
    startsIn: (d: number, h: number, m: number) => {
      const parts: string[] = [];
      if (d > 0) parts.push(`${d} ${d === 1 ? "day" : "days"}`);
      if (h > 0 && parts.length < 2) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
      if (d === 0 && m > 0 && parts.length < 2) parts.push(`${m} ${m === 1 ? "minute" : "minutes"}`);
      return `Starts in ${parts.join(" ")}`;
    },
    retakesLeft: (n: number) => `${n} remaining`,
    noRetakesLeft: "No retakes left",
    noRetakes: "No retakes",
    range: (a: number, b: number, total: number) => `${a}-${b} of ${total} assignments`,
    previous: "Previous",
    next: "Next",
    fetchError: "Failed to fetch assignments",
    updateError: "Failed to update assignment status",
  },
  es: {
    title: "Mis tareas",
    sortBy: "Ordenar por:",
    dueDate: "Fecha de entrega",
    startDate: "Fecha de inicio",
    chapter: "Capítulo",
    filterBy: "Mostrar:",
    active: "Tareas activas",
    inProgress: "En curso",
    notStarted: "Sin empezar",
    overdue: "Atrasadas",
    completed: "Terminadas",
    waiting: "Esperando al maestro",
    assignment: "Tarea",
    status: "Estado",
    grade: "Calificación",
    retakes: "Reintentos",
    action: "Acción",
    loading: "Cargando tareas...",
    none: "No hay tareas",
    daysLeft: (n: number) => (n === 1 ? "Queda 1 día" : `Quedan ${n} días`),
    dueToday: "Se entrega hoy o ya pasó la fecha",
    pending: "En revisión",
    start: "Empezar",
    continue: "Continuar",
    completeNow: "Terminar ahora",
    view: "Ver",
    feedback: "Comentarios",
    retake: "Reintentar",
    startsToday: "Empieza hoy",
    startsIn: (d: number, h: number, m: number) => {
      const parts: string[] = [];
      if (d > 0) parts.push(`${d} ${d === 1 ? "día" : "días"}`);
      if (h > 0 && parts.length < 2) parts.push(`${h} ${h === 1 ? "hora" : "horas"}`);
      if (d === 0 && m > 0 && parts.length < 2) parts.push(`${m} ${m === 1 ? "minuto" : "minutos"}`);
      return `Empieza en ${parts.join(" ")}`;
    },
    retakesLeft: (n: number) => `Quedan ${n}`,
    noRetakesLeft: "No quedan reintentos",
    noRetakes: "Sin reintentos",
    range: (a: number, b: number, total: number) => `${a}-${b} de ${total} tareas`,
    previous: "Anterior",
    next: "Siguiente",
    fetchError: "No se pudieron cargar las tareas",
    updateError: "No se pudo actualizar la tarea",
  },
};

const STATUS_KEY: Record<string, "inProgress" | "notStarted" | "overdue" | "completed" | "waiting"> = {
  in_progress: "inProgress",
  not_started: "notStarted",
  overdue: "overdue",
  completed: "completed",
  submitted: "waiting",
};

const AssignmentTable = () => {
  const [assignments, setAssignments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("due_date");
  const [feedbackModal, setFeedbackModal] = useState<{
    isOpen: boolean;
    feedback: string;
    assignmentName: string;
  }>({
    isOpen: false,
    feedback: "",
    assignmentName: "",
  });
  const user = useUserStore((state) => state.user);
  const lang = useUiLang();
  const t = TABLE_T[lang];
  const locale = lang === "es" ? "es-US" : "en-US";
  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
  const fmtTime = (d: string) =>
    new Date(d).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  // PE rows store "PE · S1 Ch2 · T3 · EN" in `standard`; show it in words.
  const chapterLabel = (asg: any) => {
    const m = /S(\d+) Ch(\d+) · (T\d)/.exec(asg?.standard || "");
    if (asg?.type !== "pe_chapter" || !m) return asg?.standard;
    return lang === "es"
      ? `Temporada ${m[1]}, capítulo ${m[2]} · ${m[3]}`
      : `Season ${m[1]}, Chapter ${m[2]} · ${m[3]}`;
  };
  // Run E2 J5: a PE chapter's name in the language of the assignment
  // (pe:<ch>:<tier>:<lang>). assessment_name holds the English title from
  // pe_chapters; the Spanish name is the approved one in peCurriculum.ts.
  const assignmentName = (asg: any) => {
    const m = /S(\d+) Ch(\d+)/.exec(asg?.standard || "");
    const asgLang = String(asg?.assessment_id || "").split(":")[3];
    if (asg?.type !== "pe_chapter" || !m || asgLang !== "es") return asg?.assessment_name;
    const ch = peCurriculum.find((c) => c.season === Number(m[1]) && c.chapter === Number(m[2]));
    return ch?.title_es || asg?.assessment_name;
  };
  const retakeText = (a: any, fallback: string) => {
    if (a.assignments?.retake_option === "oneRetake") {
      return a.retakes_remaining > 0 ? t.retakesLeft(a.retakes_remaining) : t.noRetakesLeft;
    }
    return fallback === "No retakes" ? t.noRetakes : fallback;
  };
  const navigate = useNavigate();

  const itemsPerPage = 5;

  async function getUserAssignments(page = 1) {
    if (!user?.id) return;

    setLoading(true);

    // Build the query
    let query = supabase
      .from("student_assignments")
      .select(
        `
        *,
        assignments (
          *
        )
      `,
        { count: "exact" }
      )
      .eq("student_id", user.id);

    switch (sortBy) {
      case "start_date":
        query = query.order("assignments(start_date)", { ascending: true });
        break;
      case "due_date":
        query = query.order("assignments(due_date)", { ascending: true });
        break;
      case "standard":
        query = query.order("assignments(standard)", { ascending: true });
        break;
      default:
        query = query.order("assignments(due_date)", { ascending: true });
    }

    if (activeFilter === "all") {
      query = query.in("status", [
        "in_progress",
        "not_started",
        "submitted",
      ]);
    } else {
      const filterStatus = activeFilter.replace("-", "_");
      query = query.eq("status", filterStatus);
    }

    // Apply pagination
    const from = (page - 1) * itemsPerPage;
    const to = from + itemsPerPage - 1;
    query = query.range(from, to);

    const { data: assignments, error: assignmentsError, count } = await query;

    if (assignmentsError) {
      toast.error(t.fetchError);
      setLoading(false);
      return;
    }

    let sortedAssignments = assignments || [];
    if (activeFilter === "all") {
      sortedAssignments = [...sortedAssignments].sort((a, b) => {
        if (a.status === "in_progress" && b.status !== "in_progress") return -1;
        if (a.status !== "in_progress" && b.status === "in_progress") return 1;
        return 0;
      });
    }

    setAssignments(sortedAssignments);
    setTotalCount(count || 0);
    setLoading(false);
    return sortedAssignments;
  }

  useEffect(() => {
    setCurrentPage(1);
    getUserAssignments(1);
  }, [user?.id, activeFilter, sortBy]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePrevious = async () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      await getUserAssignments(newPage);
    }
  };

  const handleNext = async () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      await getUserAssignments(newPage);
    }
  };

  const handleStartAssignment = async (status, assignmentId, route) => {
    if (!user?.id || !assignmentId) return;

    if (status === "not_started") {
      const { error } = await supabase
        .from("student_assignments")
        .update({ status: "in_progress" })
        .eq("student_id", user.id)
        .eq("assignment_id", assignmentId);

      if (error) {
        toast.error(t.updateError);
        console.error("Update failed:", error);
      } else {
        navigate(route);
      }
    } else {
      navigate(route);
    }
  };

  const handleRetakeClick = async (assignmentId, assessmentId, type) => {
    await handleRetakeAssignment(user.id, assignmentId, () => {
      getUserAssignments();
      navigate(`/${getRouteBase(type)}/${assessmentId}/${assignmentId}`);
    });
  };

  function getRouteBase(type) {
    switch (type) {
      case "midterm":
        return "midterm";
      case "final":
        return "finals";
      case "project":
        return "project";
      default:
        return "quiz";
    }
  }

  function getAssignmentUI(status) {
    switch (status?.toLowerCase()) {
      case "not_started":
        return {
          label: t.start,
          iconPath:
            "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z",
          buttonColor:
            "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
          statusDot: "bg-gray-400",
          dueColor: "text-gray-400",
          iconColor: "bg-gray-100",
          iconStroke: "text-gray-600",
        };

      case "in_progress":
        return {
          label: t.continue,
          iconPath:
            "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z",
          buttonColor: "text-white bg-indigo-600 hover:bg-indigo-700",
          statusDot: "bg-blue-600",
          dueColor: "text-blue-600",
          iconColor: "bg-blue-100",
          iconStroke: "text-blue-600",
        };

      case "overdue":
        return {
          label: t.completeNow,
          iconPath:
            "M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z",
          buttonColor: "text-white bg-red-600 hover:bg-red-700",
          statusDot: "bg-red-600",
          dueColor: "text-red-600 font-medium",
          iconColor: "bg-red-100",
          iconStroke: "text-red-600",
        };

      case "submitted":
        return {
          label: t.view,
          iconPath: "",
          buttonColor: "border border-gray-300 text-gray-700 bg-white",
          statusDot: "bg-amber-500",
          dueColor: "text-amber-600",
          iconColor: "bg-amber-100",
          iconStroke: "text-amber-600",
        };

      case "completed":
        return {
          label: t.view,
          iconPath:
            "M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z",
          buttonColor:
            "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
          statusDot: "bg-green-600",
          dueColor: "text-green-600",
          iconColor: "bg-green-100",
          iconStroke: "text-green-600",
        };

      default:
        return {
          label: t.start,
          iconPath: "",
          buttonColor: "text-gray-500 bg-white",
          statusDot: "bg-gray-300",
          dueColor: "text-gray-300",
          iconColor: "bg-gray-100",
          iconStroke: "text-gray-400",
        };
    }
  }

  return (
    <section className="mb-10">
      {/* Run E2 J2: wraps on phones (the row was wider than 390 px). */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
        <h2 className="text-lg font-medium text-gray-900">
          {t.title}
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2 relative">
            <label
              htmlFor="sort-select"
              className="text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              {t.sortBy}
            </label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-md border border-gray-300 shadow-sm pl-3 pr-10 py-2 text-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
            >
              <option value="due_date">{t.dueDate}</option>
              <option value="start_date">{t.startDate}</option>
              <option value="standard">{t.chapter}</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <div className="relative flex items-center gap-2">
            <label
              htmlFor="assignment-filter"
              className="text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              {t.filterBy}
            </label>
            <div className="relative">
              <select
                id="assignment-filter"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="rounded-md border border-indigo-400 shadow-lg shadow-indigo-200/50 pl-3 pr-10 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none transition-all hover:shadow-xl hover:shadow-indigo-300/50"
              >
                <option value="all">{t.active}</option>
                <option value="in-progress">{t.inProgress}</option>
                <option value="not-started">{t.notStarted}</option>
                <option value="overdue">{t.overdue}</option>
                <option value="submitted">{t.waiting}</option>
                <option value="completed">{t.completed}</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-indigo-500">
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-md shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t.assignment}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t.chapter}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t.startDate}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t.dueDate}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t.status}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t.grade}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t.retakes}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                {t.action}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  {t.loading}
                </td>
              </tr>
            ) : assignments.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  {t.none}
                </td>
              </tr>
            ) : (
              assignments.map((a, i) => {
                const ui = getAssignmentUI(a.status);
                const retakeInfo = getRetakeInfo(a);
                return (
                  <tr
                    key={i}
                    className={`${
                      a.highlight ? "bg-red-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div
                          className={`flex-shrink-0 h-8 w-8 rounded ${ui.iconColor} flex items-center justify-center`}
                        >
                          <svg
                            className={`h-4 w-4 ${ui.iconStroke}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">
                              {assignmentName(a.assignments)}
                            </div>
                            {a.priority && (
                              <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                {a.priority}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {a.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {chapterLabel(a.assignments)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex items-center">
                        <svg
                          className={`h-4 w-4 mr-1.5 ${ui.dueColor}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM6 7a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {fmtDate(a.assignments.start_date)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {fmtTime(a.assignments.start_date)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex items-center">
                        <svg
                          className={`h-4 w-4 mr-1.5 ${ui.dueColor}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM6 7a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {fmtDate(a.assignments.due_date)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {fmtTime(a.assignments.due_date)}
                          </div>
                          <div className={`text-xs ${ui.dueColor}`}>
                            <span>
                              {(() => {
                                const dueDate = dayjs(a.assignments.due_date);
                                const now = dayjs();
                                const daysLeft = dueDate.diff(now, "day");
                                return daysLeft > 0
                                  ? t.daysLeft(daysLeft)
                                  : t.dueToday;
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center">
                        <div
                          className={`h-2.5 w-2.5 rounded-full ${ui.statusDot} mr-2`}
                        />
                        <span className="text-sm text-gray-700">
                          {STATUS_KEY[a.status]
                            ? t[STATUS_KEY[a.status]]
                            : a.status.replace("_", " ")}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {a.is_grade_released &&
                      a.grade !== undefined &&
                      a.grade !== null ? (
                        <span className="font-bold">{a.grade}</span>
                      ) : (a.status === "completed" ||
                          a.status === "submitted") &&
                        !a.is_grade_released ? (
                        <span className="text-gray-400">{t.pending}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`text-xs font-medium ${retakeInfo.color}`}
                      >
                        {retakeText(a, retakeInfo.text)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      {(a.status === "in_progress" ||
                        a.status === "not_started") &&
                        (() => {
                          // Check if start_date exists, otherwise use created_at as fallback
                          const startDate = a.assignments.start_date
                            ? dayjs(a.assignments.start_date)
                            : dayjs(a.assignments.created_at || Date.now());
                          const now = dayjs();

                          // If start date is in the future, show countdown instead of button
                          if (startDate.isAfter(now)) {
                            const days = startDate.diff(now, "day");
                            const hours = startDate.diff(now, "hour") % 24;
                            const minutes = startDate.diff(now, "minute") % 60;

                            const countdownText =
                              days > 0 || hours > 0 || minutes > 0
                                ? t.startsIn(days, hours, minutes)
                                : t.startsToday;

                            return (
                              <div className="text-center">
                                <span className="text-sm text-gray-500">
                                  {countdownText}
                                </span>
                              </div>
                            );
                          }

                          // Otherwise show the normal button
                          return (
                            <button
                              onClick={() =>
                                handleStartAssignment(
                                  a.status,
                                  a.assignment_id,
                                  a.assignments.type === "pe_chapter"
                                    ? `/student/lesson/${a.assignment_id}`
                                    : `/${getRouteBase(a.assignments.type)}/${
                                        a.assignments.assessment_id
                                      }/${a.assignment_id}`
                                )
                              }
                              className={`flex justify-center w-full items-center px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap ${ui.buttonColor}`}
                            >
                              <svg
                                className="mr-1.5 h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d={ui.iconPath}
                                  clipRule="evenodd"
                                />
                              </svg>
                              {ui.label}
                            </button>
                          );
                        })()}
                      {(a.status === "completed" ||
                        (a.status === "submitted" && a.is_grade_released)) && (
                        <div className="flex flex-col gap-2 w-full">
                          <Link
                            to={
                              a.assignments.type === "pe_chapter"
                                ? `/student/lesson/${a.assignment_id}`
                                : `/${getRouteBase(a.assignments.type)}-review/${
                                    a.assignments.assessment_id
                                  }/${a.assignments.id}`
                            }
                            className={`flex justify-center items-center px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap ${ui.buttonColor}`}
                          >
                            <svg
                              className="mr-1.5 h-3 w-3"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d={ui.iconPath}
                                clipRule="evenodd"
                              />
                            </svg>
                            {ui.label}
                          </Link>
                          {a.teacher_feedback && (
                            <button
                              onClick={() => {
                                setFeedbackModal({
                                  isOpen: true,
                                  feedback: a.teacher_feedback,
                                  assignmentName: a.assignments.assessment_name,
                                });
                              }}
                              className="flex justify-center items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                            >
                              <svg
                                className="mr-1.5 h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {t.feedback}
                            </button>
                          )}
                          {retakeInfo.canRetake && (
                            <button
                              onClick={() =>
                                handleRetakeClick(
                                  a.assignment_id,
                                  a.assignments.assessment_id,
                                  a.assignments.type
                                )
                              }
                              disabled={dayjs(a.assignments.due_date).isBefore(
                                dayjs()
                              )}
                              className="flex justify-center items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 whitespace-nowrap disabled:opacity-50"
                            >
                              <svg
                                className="mr-1.5 h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {t.retake}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between items-center mt-4 rounded-md">
        <div className="text-sm text-gray-700">
          {assignments.length > 0
            ? t.range(
                (currentPage - 1) * itemsPerPage + 1,
                Math.min(currentPage * itemsPerPage, totalCount),
                totalCount
              )
            : t.none}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1 || loading}
            className={`px-3 py-1 border border-gray-300 rounded-md text-sm ${
              currentPage === 1 || loading
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white hover:bg-gray-50"
            }`}
          >
            {t.previous}
          </button>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages || loading}
            className={`px-3 py-1 border border-gray-300 rounded-md text-sm ${
              currentPage === totalPages || loading
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white hover:bg-gray-50"
            }`}
          >
            {t.next}
          </button>
        </div>
      </div>

      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal((prev) => ({ ...prev, isOpen: false }))}
        feedback={feedbackModal.feedback}
        assignmentName={feedbackModal.assignmentName}
      />
    </section>
  );
};

export default AssignmentTable;
